import React, { useRef } from 'react';
import { Modal, Collapse, Carousel } from 'antd';
import { fakeProImage } from '../../config/fakeData';
import { CaretLeftFilled, CaretRightFilled } from '@ant-design/icons';
import { ModalCarouselWrapper } from "./style";

const AMAZON_BASE_URL = "https://www.amazon.in/dp/";

const CustomPrevArrow = (props) => {
    const { className, style, onClick } = props;
    return <CaretLeftFilled className={className} style={{ ...style, fontSize: '40px', color: '#181C31', left: '-30px', zIndex: 1 }} onClick={onClick} />;
};

const CustomNextArrow = (props) => {
    const { className, style, onClick } = props;
    return <CaretRightFilled className={className} style={{ ...style, fontSize: '40px', color: '#181C31', right: '-5px', zIndex: 1 }} onClick={onClick} />;
};

const OrderDetailsModal = ({ open, onClose, data, currencySign }) => {
    const mainCollapseRef = useRef(null);

    const getAmount = (val) => {
        const amount = parseFloat(val || 0);
        return `${amount < 0 ? '-' : ''}${currencySign}${Math.abs(amount).toFixed(2)}`;
    };

    const handleCollapseChange = (activeKeys) => {
        if (Array.isArray(activeKeys) && activeKeys.length > 0) {
            const newlyOpenedKey = activeKeys[activeKeys.length - 1];
            const panelHeader = mainCollapseRef.current?.querySelector(`.ant-collapse-item[data-key="${newlyOpenedKey}"] .ant-collapse-header`);
            if (panelHeader) panelHeader.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    };

    const generateOrderMetrics = (orderData) => {
        if (!orderData || !orderData.more_info) return { revenueMetrics: [], costMetrics: [], profitabilityMetrics: [] };

        const info = orderData.more_info;

        const revenueMetrics = [
            { key: '1', title: 'Sales', amount: getAmount(info.sales), items: [] },
            { key: '2', title: 'Units', amount: `${info.units || 0}`, items: [] },
            { key: '3', title: 'Promo', amount: getAmount(info.promo), items: [] },
            { key: '4', title: 'Gift Wrap', amount: getAmount(info.giftwrap), items: [] },
        ];

        const costMetrics = [
            {
                key: '1',
                title: 'Amazon Fees',
                amount: getAmount(info.amazon_fees?.total),
                items: [
                    { label: 'FBA Fulfilment', value: getAmount(info.amazon_fees?.fba_per_unit_fulfilment_fee) },
                    { label: 'FBA Weight Based Fee', value: getAmount(info.amazon_fees?.fba_weight_based_fee) },
                    { label: 'Fixed Closing Fee', value: getAmount(info.amazon_fees?.fixed_closing_fee) },
                    { label: 'Item TDS', value: getAmount(info.amazon_fees?.item_TDS) },
                ]
            },
            { key: '2', title: 'Shipping Cost', amount: getAmount(-info.shipping_cost), items: [] },
            { key: '3', title: 'Cost of Goods', amount: getAmount(-info.cost_of_goods), items: [] },
            { key: '4', title: 'VAT', amount: getAmount(info.vat), items: [] },
        ];

        const profitabilityMetrics = [
            { key: '1', title: 'Gross Profit', amount: getAmount(info.gross_profit), items: [] },
            { key: '2', title: 'Net Profit', amount: getAmount(info.net_profit), items: [] },
            { key: '3', title: 'Estimated Payout', amount: getAmount(info.estimated_payout), items: [] },
        ];

        const format = (items) => items.map(section => ({
            key: section.key,
            label: (
                <div className="d-flex justify-content-between w-100">
                    <span className="text-gray-800 fw-bold fs-6">{section.title}</span>
                    <span className="text-gray-800 fw-bolder fs-6 me-3">{section.amount}</span>
                </div>
            ),
            children: section.items?.length > 0 ? (
                <div className="ps-4">
                    {section.items.map((item, i) => (
                        <div key={i} className="d-flex justify-content-between py-1 border-bottom border-bottom-dashed">
                            <span className="text-gray-600">{item.label}</span>
                            <span className="fw-bold">{item.value}</span>
                        </div>
                    ))}
                </div>
            ) : null,
            showArrow: section.items?.length > 0,
            collapsible: section.items?.length > 0 ? '' : 'disabled'
        }));

        return {
            revenue: format(revenueMetrics),
            costs: format(costMetrics),
            profitability: format(profitabilityMetrics)
        };
    };

    const { revenue, costs, profitability } = generateOrderMetrics(data);

    return (
        <ModalCarouselWrapper>
            <Modal open={open} onCancel={onClose} footer={null} width={650} centered className="custom-modal">
                {data && (
                    <div style={{ padding: '20px' }}>
                        <div className="text-center mb-4">
                            <img src={data.image_url || fakeProImage} alt="Product" style={{ maxHeight: 120, objectFit: 'contain' }} />
                            <h3 className="mt-3 fs-5 px-5">
                                <a className={'link-success cursor-pointer'} href={data.asin ? `${AMAZON_BASE_URL}${data.asin}` : "#"} target="_blank" rel="noreferrer">{data.product_name}</a>
                            </h3>
                            <div className="text-muted fs-7">Order ID: <b className="text-dark">{data.order_id}</b></div>
                        </div>

                        <Carousel arrows prevArrow={<CustomPrevArrow />} nextArrow={<CustomNextArrow />} dots={false}>
                            <div>
                                <div className="border-till border rounded p-3 px-4 mb-3"
                                     style={{borderColor: '#63CE94 !important', background: '#fff4e6'}}>
                                    <div className="d-flex align-items-center justify-content-center"><h5
                                        className="mb-0 text-green fw-semibold fs-5">Performance</h5></div>
                                </div>
                                <div className="modal-collapse-scrollable"><
                                    Collapse items={revenue} bordered={false} onChange={handleCollapseChange} ref={mainCollapseRef}/>
                                </div>
                            </div>
                            <div>
                                <div className="border-till border rounded p-3 px-4 mb-3"
                                     style={{borderColor: '#63CE94 !important', background: '#fff4e6'}}>
                                    <div className="d-flex align-items-center justify-content-center"><h5
                                        className="mb-0 text-green fw-semibold fs-5">Cost</h5></div>
                                </div>
                                <div className="modal-collapse-scrollable">
                                    <Collapse items={costs} bordered={false} onChange={handleCollapseChange} ref={mainCollapseRef}/>
                                </div>
                            </div>
                            <div>
                                <div className="border-till border rounded p-3 px-4 mb-3"
                                     style={{borderColor: '#63CE94 !important', background: '#fff4e6'}}>
                                    <div className="d-flex align-items-center justify-content-center"><h5
                                        className="mb-0 text-green fw-semibold fs-5">Profit</h5></div>
                                </div>
                                <div className="modal-collapse-scrollable">
                                    <Collapse items={profitability} bordered={false} onChange={handleCollapseChange} ref={mainCollapseRef}/>
                                </div>
                            </div>
                        </Carousel>

                        <div
                            className="mt-5 p-4 rounded border border-dashed d-flex justify-content-between align-items-center" style={{ backgroundColor: "#DAF3E4" }}>
                            <h4 className="mb-0 fw-bold" style={{ color: "#0F7E3E" }}>Net Profit</h4>
                            <h2 className="mb-0 fw-bolder" style={{ color: "#0F7E3E" }}>{getAmount(data.more_info?.net_profit)}</h2>
                        </div>
                    </div>
                )}
            </Modal>
        </ModalCarouselWrapper>
    );
};

export default OrderDetailsModal;